import React, { useState, useEffect } from 'react';
import { ComposableMap, Geographies, Geography, Annotation } from 'react-simple-maps';
import { Globe, Shield, Building } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// FIPS code to state abbreviation mapping
const fipsToState: { [key: string]: string } = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA",
  "08": "CO", "09": "CT", "10": "DE", "11": "DC", "12": "FL",
  "13": "GA", "15": "HI", "16": "ID", "17": "IL", "18": "IN",
  "19": "IA", "20": "KS", "21": "KY", "22": "LA", "23": "ME",
  "24": "MD", "25": "MA", "26": "MI", "27": "MN", "28": "MS",
  "29": "MO", "30": "MT", "31": "NE", "32": "NV", "33": "NH",
  "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND",
  "39": "OH", "40": "OK", "41": "OR", "42": "PA", "44": "RI",
  "45": "SC", "46": "SD", "47": "TN", "48": "TX", "49": "UT",
  "50": "VT", "51": "VA", "53": "WA", "54": "WV", "55": "WI",
  "56": "WY"
};

// State names for tooltip
const stateNames: { [key: string]: string } = {
  "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas", "CA": "California",
  "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware", "DC": "District of Columbia",
  "FL": "Florida", "GA": "Georgia", "HI": "Hawaii", "ID": "Idaho", "IL": "Illinois",
  "IN": "Indiana", "IA": "Iowa", "KS": "Kansas", "KY": "Kentucky", "LA": "Louisiana",
  "ME": "Maine", "MD": "Maryland", "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota",
  "MS": "Mississippi", "MO": "Missouri", "MT": "Montana", "NE": "Nebraska", "NV": "Nevada",
  "NH": "New Hampshire", "NJ": "New Jersey", "NM": "New Mexico", "NY": "New York",
  "NC": "North Carolina", "ND": "North Dakota", "OH": "Ohio", "OK": "Oklahoma", "OR": "Oregon",
  "PA": "Pennsylvania", "RI": "Rhode Island", "SC": "South Carolina", "SD": "South Dakota",
  "TN": "Tennessee", "TX": "Texas", "UT": "Utah", "VT": "Vermont", "VA": "Virginia",
  "WA": "Washington", "WV": "West Virginia", "WI": "Wisconsin", "WY": "Wyoming"
};

interface CoverageData {
  nmvtis: {
    covered: string[];
    total: number;
  };
  dmv: {
    connected: string[];
    comingSoon?: string[];
    total: number;
  };
}

interface CoverageMapSectionProps {
  showDmvConnections?: boolean;
  subtitle?: string;
}

export const CoverageMapSection: React.FC<CoverageMapSectionProps> = ({ showDmvConnections = true, subtitle }) => {
  const [activeTab, setActiveTab] = useState<'nmvtis' | 'dmv'>('nmvtis');
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [coverageData, setCoverageData] = useState<CoverageData | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch coverage data on mount
  useEffect(() => {
    const fetchCoverage = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.COVERAGE.STATES);
        const data = await response.json();
        setCoverageData(data);
      } catch (error) {
        console.error('Failed to fetch coverage data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCoverage();
  }, []);

  useEffect(() => {
    if (!showDmvConnections && activeTab === 'dmv') {
      setActiveTab('nmvtis');
    }
  }, [showDmvConnections, activeTab]);

  const isStateCovered = (stateAbbr: string) => {
    if (!coverageData) return false;
    if (activeTab === 'nmvtis') {
      return coverageData.nmvtis.covered.includes(stateAbbr);
    }
    return coverageData.dmv.connected.includes(stateAbbr);
  };

  // DMV tab only: a connection being built. The API leaves connected states out of this list.
  const isStateComingSoon = (stateAbbr: string) =>
    activeTab === 'dmv' && !!coverageData?.dmv.comingSoon?.includes(stateAbbr);

  const getStateFill = (stateAbbr: string) => {
    const covered = isStateCovered(stateAbbr);
    if (covered) {
      return activeTab === 'nmvtis' ? '#059669' : '#3b82f6';
    }
    if (isStateComingSoon(stateAbbr)) return '#d97706';
    return '#374151';
  };

  const getStateStroke = (stateAbbr: string) => {
    const covered = isStateCovered(stateAbbr);
    if (covered) {
      return activeTab === 'nmvtis' ? '#34d399' : '#60a5fa';
    }
    if (isStateComingSoon(stateAbbr)) return '#fbbf24';
    return '#475569';
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  return (
    <section className="relative bg-slate-950 py-20 px-6 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className={`inline-flex items-center space-x-2 px-4 py-2 backdrop-blur-sm rounded-full border mb-6 ${
            activeTab === 'nmvtis'
              ? 'bg-emerald-500/10 border-emerald-500/20'
              : 'bg-blue-500/10 border-blue-500/20'
          }`}>
            <Globe className={`w-4 h-4 ${activeTab === 'nmvtis' ? 'text-emerald-400' : 'text-blue-400'}`} />
            <span className={`text-sm font-semibold ${activeTab === 'nmvtis' ? 'text-emerald-300' : 'text-blue-300'}`}>
              Nationwide Coverage
            </span>
          </div>
          <h2 className="text-5xl font-black mb-4">
            <span className="text-white">
              Coast-to-Coast Data Access
            </span>
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            {subtitle || 'Comprehensive vehicle intelligence powered by direct integrations with DMVs and official NMVTIS data across all 50 states.'}
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center items-center mb-8">
          {activeTab === 'nmvtis' && (
            <img
              src="/nmvtis-logo.jpg"
              alt="NMVTIS"
              className="h-12 w-auto rounded-lg shadow-lg mr-4"
            />
          )}
          <div className="inline-flex bg-slate-800/30 backdrop-blur-sm rounded-xl p-1.5 border border-slate-700/50">
            <button
              onClick={() => setActiveTab('nmvtis')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                activeTab === 'nmvtis'
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'text-slate-400 hover:text-slate-300'
              }`}
            >
              <Shield className="w-5 h-5" />
              <span>NMVTIS Data</span>
            </button>
            {showDmvConnections && (
              <button
                onClick={() => setActiveTab('dmv')}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeTab === 'dmv'
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'text-slate-400 hover:text-slate-300'
                }`}
              >
                <Building className="w-5 h-5" />
                <span>DMV Connections</span>
              </button>
            )}
          </div>
        </div>

        {/* Map Container */}
        <div className="bg-gradient-to-br from-slate-800/30 to-slate-900/50 rounded-2xl border border-slate-700/50 p-6 mb-8 max-w-5xl mx-auto">
          {/* Map Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h3 className="text-2xl font-black text-white mb-2">
                {activeTab === 'nmvtis' ? 'NMVTIS Data Coverage' : 'Direct DMV Connections'}
              </h3>
              <p className="text-slate-400">
                {activeTab === 'nmvtis'
                  ? 'Access to the National Motor Vehicle Title Information System'
                  : 'Real-time connections to state Department of Motor Vehicles'}
              </p>
            </div>
            <div className={`px-4 py-2 rounded-lg border ${
              activeTab === 'nmvtis'
                ? 'bg-emerald-500/20 border-emerald-500/30'
                : 'bg-blue-500/20 border-blue-500/30'
            }`}>
              <span className={`text-lg font-black ${
                activeTab === 'nmvtis' ? 'text-emerald-300' : 'text-blue-300'
              }`}>
                {loading ? '...' : (activeTab === 'nmvtis'
                  ? `${coverageData?.nmvtis.total || 0} State${coverageData?.nmvtis.total === 1 ? '' : 's'}`
                  : `${coverageData?.dmv.total || 0} State${coverageData?.dmv.total === 1 ? '' : 's'} Active`
                )}
              </span>
            </div>
          </div>

          {/* US Map */}
          <div className="relative" onMouseMove={handleMouseMove}>
            {loading ? (
              <div className="flex items-center justify-center h-96">
                <div className={`w-8 h-8 border-4 border-t-transparent rounded-full animate-spin ${
                  activeTab === 'nmvtis' ? 'border-emerald-500' : 'border-blue-500'
                }`}></div>
              </div>
            ) : (
              <ComposableMap
                projection="geoAlbersUsa"
                projectionConfig={{
                  scale: 1000,
                }}
                style={{ width: '100%', height: '700px' }}
              >
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies
                      .filter((geo) => {
                        // Only render valid US states + DC (excludes territories and islands)
                        const stateAbbr = fipsToState[geo.id];
                        return stateAbbr !== undefined;
                      })
                      .map((geo) => {
                        const fipsCode = geo.id;
                        const stateAbbr = fipsToState[fipsCode];

                        return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          fill={getStateFill(stateAbbr)}
                          stroke={getStateStroke(stateAbbr)}
                          strokeWidth={isStateCovered(stateAbbr) ? 1.5 : 1}
                          style={{
                            default: { outline: 'none' },
                            hover: {
                              fill: isStateCovered(stateAbbr)
                                ? activeTab === 'nmvtis' ? '#10b981' : '#60a5fa'
                                : isStateComingSoon(stateAbbr) ? '#f59e0b' : '#374151',
                              outline: 'none',
                              cursor: isStateCovered(stateAbbr) ? 'pointer' : 'default',
                            },
                            pressed: { outline: 'none' },
                          }}
                          onMouseEnter={() => setHoveredState(stateAbbr)}
                          onMouseLeave={() => setHoveredState(null)}
                        />
                      );
                    })
                  }
                </Geographies>

                {/* DC is a pixel or two at this scale, under Maryland's and Virginia's borders, so it
                    gets a labelled marker off the coast, coloured and hovered like any state. */}
                <Annotation
                  subject={[-77.03, 38.9]}
                  dx={70}
                  dy={30}
                  curve={0}
                  connectorProps={{ stroke: '#64748b', strokeWidth: 1 }}
                >
                  <g
                    onMouseEnter={() => setHoveredState('DC')}
                    onMouseLeave={() => setHoveredState(null)}
                    style={{ cursor: isStateCovered('DC') ? 'pointer' : 'default' }}
                  >
                    <rect
                      x={0}
                      y={-11}
                      width={34}
                      height={22}
                      rx={4}
                      fill={getStateFill('DC')}
                      stroke={getStateStroke('DC')}
                      strokeWidth={isStateCovered('DC') ? 1.5 : 1}
                    />
                    <text x={17} y={4} textAnchor="middle" fontSize={12} fontWeight={700} fill="#f8fafc">
                      DC
                    </text>
                  </g>
                </Annotation>
              </ComposableMap>
            )}

            {/* Tooltip */}
            {hoveredState && (
              <div
                className="fixed z-50 pointer-events-none"
                style={{
                  left: tooltipPosition.x + 10,
                  top: tooltipPosition.y - 40,
                }}
              >
                <div className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 shadow-xl">
                  <div className="text-sm font-bold text-white mb-1">
                    {stateNames[hoveredState] || hoveredState}
                  </div>
                  <div className={`text-xs font-semibold flex items-center space-x-1 ${
                    isStateCovered(hoveredState)
                      ? (activeTab === 'nmvtis' ? 'text-emerald-300' : 'text-blue-300')
                      : isStateComingSoon(hoveredState) ? 'text-amber-300' : 'text-slate-400'
                  }`}>
                    <span>
                      {isStateCovered(hoveredState)
                        ? '✓ Active'
                        : isStateComingSoon(hoveredState) ? '◐ Coming Soon' : '○ Not Available'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center space-x-6 mt-8 pt-6 border-t border-slate-700/50">
            <div className="flex items-center space-x-2">
              <div className={`w-4 h-4 rounded ${
                activeTab === 'nmvtis' ? 'bg-emerald-600' : 'bg-blue-500'
              }`}></div>
              <span className="text-sm text-slate-300 font-medium">Active Coverage</span>
            </div>
            {activeTab === 'dmv' && (
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 rounded bg-amber-600"></div>
                <span className="text-sm text-slate-300 font-medium">Coming Soon</span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-gray-600"></div>
              <span className="text-sm text-slate-300 font-medium">Not Available</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
